import {
  PageTemplate,
  PDFAnalysisSectionDTO,
  PDFFeatureAnalysis,
} from "@pages/platform-analysis/components/buttons/export-pdf/model";
import {
  MARGIN_DEFAULT,
  MAX_PAGE_INDEX_TO_SHOW_MAIN_HEADER,
  PAGE_BODY_HEIGHT,
  PAGE_BODY_HEIGHT_WITHOUT_HEADING,
  TABLE_ROW_HEADER_LINE,
  TITLE_HEIGHT,
} from "@pages/platform-analysis/components/buttons/export-pdf/constant";
import { PDFFeatureAnalysisSection } from "@pages/platform-analysis/components/buttons/export-pdf/components/sections/feature-analysis/index";
import { cloneDeep, isEmpty } from "lodash";
import { pagesHandler } from "@pages/platform-analysis/components/buttons/export-pdf/util";
import { FieldTypeId } from "@models/configuration";
import { PDFFeatureAnalysisTableConfigs as Configs } from "@pages/platform-analysis/components/buttons/export-pdf/components/sections/feature-analysis/configs";

const calculateAvailableRowCount = ({
  rowsData,
  remainingHeight,
}: {
  rowsData: PDFAnalysisSectionDTO[];
  remainingHeight: number;
}): {
  rowCount: number;
  totalRowHeight: number;
} => {
  let isIncludedFooterHeight = false;
  let totalRowHeight = 0;
  let rowCount = 0;

  for (const row of rowsData) {
    const rowName = row?.name || "";
    const rowHeight =
      rowName.length > Configs.MaxCharsPerLine
        ? Configs.DoubleRowHeight
        : Configs.SingleRowHeight;

    if (!isIncludedFooterHeight) {
      const hasFooter = row.analysisData?.some(
        (item) => item.fieldTypeId === FieldTypeId.TextLong,
      );

      if (hasFooter) {
        totalRowHeight += Configs.TableFooterHeight;
        isIncludedFooterHeight = true;
      }
    }

    totalRowHeight += rowHeight;

    if (totalRowHeight <= remainingHeight) {
      rowCount++;
    } else {
      break;
    }
  }

  return { rowCount, totalRowHeight };
};

export const processFeatureAnalysisSectionData = ({
  spaceAvailable,
  sections,
  isTableExpand,
  isBusinessSection,
  isDetailMode,
}: {
  spaceAvailable: number;
  sections: PDFAnalysisSectionDTO[];
  isTableExpand: boolean;
  isBusinessSection?: boolean;
  isDetailMode?: boolean;
}) => {
  const overscoreHeight =
    isBusinessSection || isDetailMode
      ? Configs.ExtendOverscoreHeight
      : Configs.OverscoreHeight;

  const additionalHeight =
    TITLE_HEIGHT +
    (TABLE_ROW_HEADER_LINE + 5) +
    (!isTableExpand ? overscoreHeight : 0);

  const remainingHeight = spaceAvailable - additionalHeight;
  const { rowCount, totalRowHeight } = calculateAvailableRowCount({
    rowsData: sections,
    remainingHeight,
  });

  let occupyHeight = 0;
  let hasContinue = false;
  let dataSeparated: PDFAnalysisSectionDTO[] = [];

  if (rowCount > 0) {
    if (sections.length > rowCount) {
      dataSeparated = sections.splice(0, rowCount);
      occupyHeight = spaceAvailable;
      hasContinue = true;
    } else if (sections.length <= rowCount) {
      dataSeparated = sections;
      occupyHeight = totalRowHeight + additionalHeight;
      hasContinue = false;
    }
  } else {
    hasContinue = true;
  }

  return {
    occupyHeight: occupyHeight,
    data: dataSeparated,
    hasContinue: hasContinue,
  };
};

export const featureAnalysisPageHandler = ({
  pages,
  spaceAvailable,
  featureAnalysis,
  showSubSection,
  isDetailMode,
  showAnalysisScore,
}: {
  pages: PageTemplate[];
  spaceAvailable: number;
  featureAnalysis?: PDFFeatureAnalysis;
  showSubSection?: boolean;
  isDetailMode?: boolean;
  showAnalysisScore?: boolean;
}) => {
  if (!featureAnalysis || isEmpty(featureAnalysis.sections)) {
    return spaceAvailable;
  }
  const isIncludeMainHeader =
    pages.length <= MAX_PAGE_INDEX_TO_SHOW_MAIN_HEADER;
  const pageBodyHeight = isIncludeMainHeader
    ? PAGE_BODY_HEIGHT
    : PAGE_BODY_HEIGHT_WITHOUT_HEADING;
  const isAppend =
    spaceAvailable > pageBodyHeight / 2 && spaceAvailable !== pageBodyHeight;

  spaceAvailable = isAppend ? spaceAvailable : pageBodyHeight;

  const featureAnalysisComp = (
    currSections: PDFAnalysisSectionDTO[],
    isTableExpand: boolean,
  ) => {
    return (
      <PDFFeatureAnalysisSection
        showSubSection={showSubSection}
        isTableExpand={isTableExpand}
        featureAnalysis={
          {
            ...featureAnalysis,
            sections: currSections,
          } as PDFFeatureAnalysis
        }
        isDetailMode={isDetailMode}
        showAnalysisScore={showAnalysisScore}
      />
    );
  };

  const sections = cloneDeep(featureAnalysis?.sections);

  let sectionProcessed = processFeatureAnalysisSectionData({
    spaceAvailable,
    sections,
    isTableExpand: false,
    isBusinessSection: false,
    isDetailMode,
  });

  pagesHandler(
    pages,
    featureAnalysisComp(sectionProcessed.data, false),
    isAppend,
  );

  while (sectionProcessed.hasContinue && !isEmpty(sections)) {
    spaceAvailable = PAGE_BODY_HEIGHT_WITHOUT_HEADING;
    sectionProcessed = processFeatureAnalysisSectionData({
      spaceAvailable,
      sections,
      isTableExpand: true,
      isBusinessSection: false,
      isDetailMode,
    });

    pagesHandler(
      pages,
      featureAnalysisComp(sectionProcessed.data, true),
      false,
    );
  }

  return spaceAvailable - sectionProcessed.occupyHeight - MARGIN_DEFAULT;
};
